import type { TeamSessionSummaryResponse } from '@frontend/core/lib/domains/reporting/types'
import { Card } from '@frontend/ui'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useReportingState } from '../lib/reporting-state'
import { buildMetrics, type ChartMetric } from './dashboard-chart-utils'

interface DashboardChartProps {
  summary?: TeamSessionSummaryResponse
}

const levelColors = {
  green: 'bg-emerald-500',
  orange: 'bg-amber-500',
  red: 'bg-red-500'
} as const

function ChartBar({ metric }: { metric: ChartMetric }) {
  const unit = metric.unit ? ` ${metric.unit}` : ''
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)
  const [averageCursor, setAverageCursor] = useState<{
    x: number
    y: number
  } | null>(null)

  const handleTooltip = (e: React.MouseEvent, remove = false) => {
    e.stopPropagation()
    setCursor(remove ? null : { x: e.clientX, y: e.clientY })
  }

  const handleAverageTooltip = (e: React.MouseEvent, remove = false) => {
    e.stopPropagation()
    setAverageCursor(remove ? null : { x: e.clientX, y: e.clientY })
    setCursor(null)
  }

  return (
    <div
      className='relative flex h-[170px] w-4 items-end justify-center'
      onMouseEnter={handleTooltip}
      onMouseMove={handleTooltip}
      onMouseLeave={(e) => handleTooltip(e, true)}
    >
      <div className='relative h-full w-full'>
        <div
          className={`absolute bottom-0 w-full rounded-sm ${levelColors[metric.level]}`}
          style={{ height: `${metric.barHeight}%` }}
        />
        {metric.refMarkerPos != null && (
          <div
            onMouseEnter={handleAverageTooltip}
            onMouseMove={handleAverageTooltip}
            onMouseLeave={(e) => handleAverageTooltip(e, true)}
            className='absolute left-1/2 h-3.5 w-3.5 rounded-full bg-foreground'
            style={{
              bottom: `${metric.refMarkerPos}%`,
              transform: 'translate(-50%, 50%)'
            }}
          />
        )}
      </div>
      {cursor &&
        createPortal(
          <div
            role='tooltip'
            className='bg-popover text-popover-foreground pointer-events-none fixed z-50 rounded-md border px-3 py-2 shadow-md'
            style={{ left: cursor.x + 12, top: cursor.y + 12 }}
          >
            <div className='text-xs font-semibold whitespace-nowrap'>
              {metric.name}
            </div>
            <div className='text-muted-foreground text-xs whitespace-nowrap'>
              This session: {metric.sessionValue}
              {unit}
            </div>
            <div className='text-muted-foreground text-xs whitespace-nowrap'>
              Season max: {metric.seasonMaxValue}
              {unit}
            </div>
          </div>,
          document.body
        )}
      {averageCursor &&
        createPortal(
          <div
            role='tooltip'
            className='bg-popover text-popover-foreground pointer-events-none fixed z-50 rounded-md border px-3 py-2 shadow-md'
            style={{ left: averageCursor.x + 12, top: averageCursor.y + 12 }}
          >
            <div className='text-xs font-semibold whitespace-nowrap'>
              {metric.name}
            </div>
            <div className='text-muted-foreground text-xs whitespace-nowrap'>
              Season average: {metric.seasonAvgValue}
              {unit}
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export function DashboardChart({ summary }: DashboardChartProps) {
  const { config } = useReportingState()
  const m = summary?.summaryMetrics
  const metrics = useMemo(
    () => (m ? buildMetrics(m, config.dashboard.chartMetrics, config) : []),
    [m, config]
  )

  return (
    <Card className='gap-6 p-4'>
      <h2 className='text-card-foreground text-base font-semibold'>
        Team performance against season average
      </h2>
      {metrics.length === 0 ? (
        <div className='flex h-64 w-full items-center justify-center'>
          <p className='text-muted-foreground'>No session data available</p>
        </div>
      ) : (
        <div className='overflow-x-auto overflow-y-hidden pt-2 pb-1'>
          <div
            className='grid min-w-[1100px]'
            style={{
              gridTemplateRows: '170px 20px 36px',
              gridAutoColumns: '1fr',
              gridAutoFlow: 'column',
              rowGap: '12px'
            }}
          >
            {metrics.map((metric) => (
              <div
                key={metric.name}
                className='grid justify-items-center'
                style={{
                  gridRow: 'span 3',
                  gridTemplateRows: 'subgrid',
                  rowGap: '12px'
                }}
              >
                <ChartBar metric={metric} />
                <div className='text-card-foreground self-center text-center text-sm font-bold tabular-nums'>
                  {metric.displayValue}
                </div>
                <p className='text-muted-foreground w-full text-center text-xs leading-none self-end px-2'>
                  {metric.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}
